import * as XLSX from "xlsx";
import { DriverReport } from "../model/report/driverReport";
import { activityAbbreviations } from "../common/enums";
import {
  TransferSummaryExcelRow,
  TransferSummaryReport,
} from "../model/report/transferSummaryReport";
import * as moment from "moment";
import { KPIAssignment, KPIReport } from "../model/report/kpiReport";
import {
  Driver,
  FlightReport,
  FlightReportActivity,
  FlightReportExcelRow,
} from "../model/report/flightReport";

export class ExcelReportWriterHelper {
  public static exportDriverReport(
    driverReports: DriverReport[],
    date: Date
  ): void {
    const workBook: XLSX.WorkBook = XLSX.utils.book_new();

    for (let report of driverReports) {
      let excelWorkSheetName = report.driverName;
      let excelWorkSheetForDriver = this.getExcelWorkSheetWithDriverReport(
        report,
        date
      );

      XLSX.utils.book_append_sheet(
        workBook,
        excelWorkSheetForDriver,
        excelWorkSheetName
      );
    }

    XLSX.writeFile(workBook, "förarrapport.xlsx");
  }

  private static getExcelWorkSheetWithDriverReport(
    driverReport: DriverReport,
    date: Date
  ): XLSX.WorkSheet {
    let workSheetData = [
      ["Förarrapport " + driverReport.driverName, date.toLocaleDateString()],
      [],
      ["Skiftlängd", driverReport.totalShiftDuration],
      ["Körtid", driverReport.totalAssignedDuration],
      ["Antal uppdrag", driverReport.assignments.length],
    ];

    workSheetData.push([""]);
    workSheetData.push(["Namn", "Typ", "Påbörjades", "Avslutades"]);
    driverReport.assignments.forEach((assignment) => {
      workSheetData.push([""]);
      workSheetData.push([assignment.description]);

      assignment.activities.forEach((activity) => {
        let abb = activityAbbreviations.find((x) => x.state === activity.type);
        abb = abb ? abb.translated : activity.type;
        let startedDateTime = "";
        //Start
        if (activity.started !== null && activity.started !== undefined) {
          startedDateTime = new Date(activity.started).toLocaleTimeString();
        }

        //End
        let endDateTime = "";
        if (activity.ended !== null && activity.ended !== undefined) {
          endDateTime = new Date(activity.ended).toLocaleTimeString();
        }

        workSheetData.push(["", abb, startedDateTime, endDateTime]);
      });
    });
    return XLSX.utils.aoa_to_sheet(workSheetData);
  }

  public static exportTransferSummary(
    transferSummaryReport: TransferSummaryReport,
    gate: string,
    from: Date,
    to: Date,
    hideFinished: boolean,
    hideNoShow: boolean
  ): void {
    let excelData: any[] = [];

    transferSummaryReport.transferSummaries.forEach((summary) => {
      summary.details.forEach((detail) => {
        excelData.push(
          new TransferSummaryExcelRow(
            summary.dateKey,
            detail.finished,
            detail.noShow,
            detail.gate
          )
        );
      });
    });

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);

    ws["A1"].v = "Datum";
    ws["B1"].v = "Utförda";
    ws["C1"].v = "BOM";
    ws["D1"].v = "Gate";
    ws["B1"].s = {
      fill: {
        patternType: "solid", // none / solid
        fgColor: { rgb: "FFD3D3D3" },
        bgColor: { rgb: "FFD3D3D3" },
      },
    };

    ws["!cols"] = [];

    // hide columns
    ws["!cols"][1] = hideFinished ? { hidden: true } : null;
    ws["!cols"][2] = hideNoShow ? { hidden: true } : null;
    ws["!cols"][3] = { wch: 8 };
    XLSX.utils.book_append_sheet(wb, ws, "transfers");

    let name: string =
      "Transfer summary " +
      moment(from).format("YYYY-MM-DD").toString() +
      "-" +
      moment(to).format("YYYY-MM-DD").toString();

    if (gate != "Alla") {
      name += "[" + gate + "]";
    }

    XLSX.writeFile(wb, name + ".xlsx");
  }

  public static exportInvoiceReport(
    invoiceData: any,
    from: Date,
    to: Date
  ): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(invoiceData);

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    XLSX.writeFile(
      wb,
      "invoices_" +
        moment(from).format("YYYY-MM-DD").toString() +
        "-" +
        moment(to).format("YYYY-MM-DD").toString() +
        ".xlsx"
    );
  }

  public static exportKpiReport(datasets: KPIReport, date: Date): void {
    let startDate: Date = new Date();
    startDate.setHours(0);
    startDate.setMinutes(0);
    startDate.setSeconds(0);

    let headers = [
      "Timme",
      "Förare",
      "Ankomster",
      "Avgångar",
      "Transfer",
      "Bussbokningar",
      "Rast",
    ];
    let data = [
      headers,
      ...datasets.assignments.map(
        (assignment: KPIAssignment, index: number) => [
          `${index}-${index + 1}`,
          assignment.drivers,
          assignment.airsideArrivals,
          assignment.airsideDepartures,
          assignment.transfers,
          assignment.busBookings,
          assignment.admin,
        ]
      ),
    ];
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data, {
      skipHeader: true,
    });
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "KPI");

    XLSX.writeFile(
      wb,
      "Historisk rapport " + moment(date).format("YYYY-MM-DD") + ".xlsx"
    );
  }

  public static exportFlightReport(
    flightReports: FlightReport[],
    startDate: Date,
    endDate: Date
  ) {
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    let excelRows: FlightReportExcelRow[] = [];

    flightReports.forEach((report: FlightReport) => {
      let flattenedActivityList = report.drivers
        .map((d: Driver) => d.activities)
        .reduce((flattenedList, activityListForDriver) => {
          const sorted = activityListForDriver.sort(
            (act1: FlightReportActivity, act2: FlightReportActivity) =>
              new Date(act1.started).getTime() >
              new Date(act2.started).getTime()
                ? 1
                : -1
          );
          return [...flattenedList, ...sorted];
        }, []);

      flattenedActivityList.forEach((activity: FlightReportActivity) => {
        let excelRow = new FlightReportExcelRow();
        excelRow.status = activity.type;
        excelRow.startedDate = moment(activity.started).format("YYYY-MM-DD");
        excelRow.startedTime = moment(activity.started).format("HH:mm");
        excelRow.ended = activity.ended ? moment(activity.ended).format("YYYY-MM-DD HH:mm") : "";
        excelRow.driver = activity.driver;
        excelRow.flight_number = report.flightData.flightId;
        excelRow.direction = report.flightData.isArrival ? "-A" : "-D";
        excelRow.planned_time = moment(report.flightData.scheduledTime).format(
          "HH:mm"
        );

        excelRows.push(excelRow);
      });
    });

    const sheetName = `${moment(startDate).format("YYYY-MM-DD")} - ${moment(
      endDate
    ).format("YYYY-MM-DD")}`;
    const reportName = `Flygrapport ${sheetName}.xlsx`;

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelRows);

    ws["!cols"] = [];
    for (let i = 0; i < 8; i++) {
      ws["!cols"][i] = { wch: 24 };
    }
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    ws["A1"].v = "Status";
    ws["B1"].v = "Startdatum";
    ws["C1"].v = "Starttid";
    ws["D1"].v = "Avslut";
    ws["E1"].v = "Förare";
    ws["F1"].v = "Flygnummer";
    ws["G1"].v = "Riktning";
    ws["H1"].v = "Planerad Tid";

    XLSX.writeFile(wb, reportName);
  }
}
